import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useStores } from "@/stores/useStores";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { billingGuardMessage } from "@/domains/billing/messages";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { IssueStatus } from "@/domains/pm/types";
import { initialDraftValues, type IssueDraftValues } from "@/domains/pm/issueDraft";
import { newIssueRefusal } from "@/domains/pm/useNewIssueGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { permissionDeniedMessage } from "@/lib/permissionDenied";
import type { StagedAttachment } from "./IssueAttachmentsStager";
import { IssueDraftContent } from "./IssueDraftContent";
import {
  submitIssueForm,
  type PrefilledDrawingAnchor,
  type PrefilledElement,
} from "./submitIssueForm";

export type { PrefilledDrawingAnchor, PrefilledElement };

interface IssueFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: number;
  /**
   * The project the issue is filed in. The form offers no project choice:
   * every entry point already knows its project (the scoped /issues and
   * My Issues pages, the viewers, a brief, a parent issue). Left unset, a BIM
   * element's own project is used. The caller refuses a member who may not
   * file issues there before opening the form (useNewIssueGuard); the form
   * itself only holds Create back if that was skipped.
   */
  defaultProjectId?: string;
  /** Status the form starts on. Used by the per-column "add card" affordance
   *  in the kanban so a new issue lands in the clicked column. Defaults to
   *  "in_progress". */
  defaultStatus?: IssueStatus;
  /** Prefilled when the form is opened from a Gantt band grouped by assignee
   *  or discipline — the band IS the value, exactly as a kanban column is. */
  defaultAssigneeIds?: number[];
  defaultDisciplineIds?: number[];
  /** WHA-3331 / WHA-3389: when set, the new issue is created as a subissue
   *  of this parent (the "Add subissue" entry points). When undefined, the
   *  header's "+" menu offers "Mark as subissue". */
  parentIssueId?: number | null;
  /** WHA-3521: pre-fill + auto-link a BIM element (from the 3D viewer). */
  prefilledElement?: PrefilledElement;
  /** Persist an exact native-DWG coordinate with the newly-created issue. */
  prefilledDrawingAnchor?: PrefilledDrawingAnchor;
  /** Existing Franklin documents staged when the form opens. Used by the DWG
   * viewer so its source drawing is linked without a second picker. */
  initialAttachments?: StagedAttachment[];
  /** Seeds the title — e.g. the brief appendix or finding it is filed from. */
  defaultTitle?: string;
  /**
   * Fires once with the new issue's id after a successful create, before the
   * dialog closes — the caller then links whatever it opened the form from (a
   * brief appendix, a finding) to that issue. A failure here never holds the
   * dialog open — the issue exists either way — so the callback reports its
   * own errors.
   */
  onCreated?: (issueId: number) => void | Promise<void>;
  /** Extra classes for the dialog content + backdrop — lets the 3D viewer
   * elevate it above the fullscreen preview overlay (WHA-3729 follow-up). */
  contentClassName?: string;
  overlayClassName?: string;
}

/**
 * The New issue modal, drawn as the expanded issue view the 3D viewer and the
 * drawing compare open (IssueDetailContent in a borderless `p-0` dialog), so the
 * issue being written looks like the issue it becomes. The fields are
 * IssueDraftContent — shared with the /issues draft panel — and this dialog owns
 * their values, the explicit Cancel / Create footer and the save.
 */
export const IssueFormDialog = observer(function IssueFormDialog({
  open,
  onOpenChange,
  buildingId,
  defaultProjectId,
  defaultStatus,
  defaultAssigneeIds,
  defaultDisciplineIds,
  parentIssueId,
  prefilledElement,
  prefilledDrawingAnchor,
  initialAttachments,
  defaultTitle,
  onCreated,
  contentClassName,
  overlayClassName,
}: IssueFormDialogProps) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const issuesStore = useIssuesStore();
  const rootStore = useStores();
  const { currentOrganization } = useOrganizationContext();
  const { canManagePMOnProject } = usePermissions();

  const [values, setValues] = useState<IssueDraftValues>(() => initialDraftValues());
  const change = useCallback(
    (patch: Partial<IssueDraftValues>) => setValues((prev) => ({ ...prev, ...patch })),
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // An upload in the attachments band has not landed: Create would file the
  // issue without it, and closing would orphan its document.
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // If a follow-up association fails after createIssue succeeds, keep the
  // dialog on that issue until its idempotent associations finish. This avoids
  // both duplicate issues and a committed issue that silently lost context.
  const createdIssueIdRef = useRef<number | null>(null);
  // The issue a retry has already written the checklist to, so a failed
  // follow-up write does not duplicate it.
  const checklistSavedForRef = useRef<number | null>(null);
  /** Offer the saved-view toggle only when a cut was actually live. */
  const showViewpointToggle = Boolean(prefilledElement?.viewpoint?.section);

  // The retry keys belong to one opening of the dialog, so they are cleared as
  // it OPENS. Clearing them as it closed raced a save still running: the save
  // wrote its id back afterwards, and the next issue was filed onto that one.
  useEffect(() => {
    if (open) {
      createdIssueIdRef.current = null;
      checklistSavedForRef.current = null;
    }
  }, [open]);

  // The caller's project, or — opened from a 3D element (WHA-3521) — the
  // owning IFC document's. Never another one: with no project choice in the
  // form, a substitute would file the issue somewhere the person did not start
  // it from.
  const projectId =
    defaultProjectId != null && defaultProjectId !== ""
      ? Number(defaultProjectId)
      : (prefilledElement?.ifc_project_id ?? null);

  // Reseeded on every open, and whenever an input changes — which is why
  // callers memoise the arrays they pass.
  useEffect(() => {
    if (open) {
      setValues(
        initialDraftValues({
          title: defaultTitle,
          status: defaultStatus,
          projectId,
          assigneeIds: defaultAssigneeIds,
          disciplineIds: defaultDisciplineIds,
          parentIssueId: parentIssueId ?? null,
          stagedAttachments: initialAttachments,
          // A new issue starts today (the default) unless the user says
          // otherwise. Left empty, it landed in the Gantt with no place on the
          // axis. saveViewpoint starts ticked again too: the dialog stays
          // mounted between issues in the 3D viewer, so an unticked "save with
          // section" would silently follow every later issue.
        })
      );
    }
    setError(null);
    setIsUploading(false);
  }, [
    open,
    projectId,
    defaultStatus,
    defaultAssigneeIds,
    defaultDisciplineIds,
    parentIssueId,
    prefilledElement,
    initialAttachments,
    defaultTitle,
  ]);

  // Every dismissal — Cancel, the header X, Escape, the overlay — goes through
  // here. Refused while a save or an upload is running (either would finish
  // against a closed form), and once the core issue exists, since closing on an
  // association error would throw away the only retry key. The success path
  // below closes through the parent callback directly.
  const mayClose = !isSubmitting && !isUploading;
  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && (!mayClose || createdIssueIdRef.current != null)) return;
    onOpenChange(nextOpen);
  };

  // Defensive: the entry points refuse before opening (useNewIssueGuard), so
  // this only shows if one did not — Create stays off and says why, rather
  // than failing on the insert policy or filing into another project. Read off
  // the props, not `values`, which are seeded a render after opening.
  const refusal = newIssueRefusal(projectId, canManagePMOnProject);
  const refusalMessage =
    refusal === "noProject"
      ? t.pm.issues.draft.noProject
      : refusal === "denied"
        ? permissionDeniedMessage(t.common.permissions, "manageIssues", "project").title
        : null;

  const canCreate = !isSubmitting && !isUploading && values.title.trim() !== "" && refusal === null;

  const handleSubmit = async () => {
    if (!canCreate || values.projectId == null) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await submitIssueForm({
        issuesStore,
        buildingId,
        orgId: currentOrganization?.id,
        userProfileId: rootStore.userStore.userProfile?.id,
        title: values.title.trim(),
        status: values.status,
        startDate: values.startDate,
        dueDate: values.dueDate,
        projectId: String(values.projectId),
        assigneeIds: values.assigneeIds,
        disciplineIds: values.disciplineIds,
        parentIssueId: values.parentIssueId,
        stagedAttachments: values.stagedAttachments,
        checklistItems: values.checklistItems ?? [],
        checklistSavedForRef,
        prefilledElement,
        prefilledDrawingAnchor,
        saveViewpoint: values.saveViewpoint,
        createdIssueIdRef,
        onViewpointSaveFailed: (vpErr) =>
          toast({
            title: t.pm.issues.viewpointSaveFailed,
            description: vpErr instanceof Error ? vpErr.message : undefined,
            variant: "destructive",
          }),
      });

      // Read before the close below: closing resets createdIssueIdRef. The
      // follow-up's failure must not block the close: the issue is committed,
      // and the form's retry key would otherwise trap the user in a dialog
      // whose Create re-runs only the follow-up. The caller toasts its own error.
      if (onCreated && createdIssueIdRef.current != null) {
        try {
          await onCreated(createdIssueIdRef.current);
        } catch {
          // Reported by the caller (see above); the issue itself was created.
        }
      }

      onOpenChange(false);
    } catch (err) {
      setError(
        billingGuardMessage(err, t) ?? (err instanceof Error ? err.message : t.common.error)
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <DialogFooter
      className={cn(
        "flex-shrink-0 gap-2 border-t px-5 py-3 sm:items-center sm:space-x-0",
        showViewpointToggle && "sm:justify-between"
      )}
    >
      {/* Only offered when a cut was live — without one the saved view would
          say nothing the element link doesn't already say. It sits with the
          actions rather than among the fields because it modifies what Create
          does; the explanation is a tooltip so the footer stays one line. */}
      {showViewpointToggle && (
        <Tooltip>
          <TooltipTrigger asChild>
            <label
              htmlFor="save-viewpoint"
              className="flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground"
            >
              <Checkbox
                id="save-viewpoint"
                checked={values.saveViewpoint}
                disabled={isSubmitting}
                onCheckedChange={(v) => change({ saveViewpoint: v === true })}
              />
              {t.pm.issues.saveWithSection}
            </label>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            {t.pm.issues.saveWithSectionHint}
          </TooltipContent>
        </Tooltip>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleDialogOpenChange(false)}
          disabled={!mayClose}
        >
          {t.pm.common.cancel}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={!canCreate}
          title={
            refusalMessage ??
            (values.title.trim() === "" ? t.pm.issues.draft.createNeedsDescription : undefined)
          }
        >
          {isSubmitting ? t.pm.issues.draft.creating : t.pm.issues.draft.create}
        </Button>
      </div>
    </DialogFooter>
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        data-issue-panel
        hideCloseButton
        overlayClassName={overlayClassName}
        // The expanded issue modal's host (IfcViewer, DrawingCompare), as a
        // column: the header and the Cancel / Create row stay put while the
        // fields scroll, so Create is never below the fold.
        className={cn("flex max-h-[85vh] max-w-3xl flex-col overflow-y-auto p-0", contentClassName)}
        onEscapeKeyDown={(e) => {
          // Radix hears Escape on the document before any field does, so the
          // checklist composer's own Escape (clear the line) would close the
          // dialog instead.
          if (e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">{t.pm.issues.draft.newIssue}</DialogTitle>
        <IssueDraftContent
          buildingId={buildingId}
          values={values}
          onChange={change}
          onClose={() => handleDialogOpenChange(false)}
          closeDisabled={!mayClose}
          onUploadingChange={setIsUploading}
          footer={footer}
          prefilledElement={prefilledElement}
          onTitleEnter={() => void handleSubmit()}
          onEnterSave={() => void handleSubmit()}
          error={error ?? refusalMessage}
          disabled={isSubmitting}
          withinDialog
        />
      </DialogContent>
    </Dialog>
  );
});
